import type { ConnectHoursEntry, HoursOfOperationDetail, HoursSchedule } from "./configuration.js";

/**
 * ADR-0019: a schedule's weekly windows in Connect's own terms, and back.
 * One implementation for the customer's stack (which writes them) and the
 * portal (which shows what Connect has and marks a difference). Connect's
 * day is a name, `00:00` as an end means midnight, a window that runs
 * past midnight is two entries, and a full day is `00:00`-`00:00`.
 */
export const CONNECT_DAYS = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"] as const;
export type ConnectDay = (typeof CONNECT_DAYS)[number];

const toMin = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
const hhmm = (m: number) => `${String(Math.floor((m % 1440) / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

export function weeklyToConnect(weekly: HoursSchedule["weekly"]): ConnectHoursEntry[] {
  const out: ConnectHoursEntry[] = [];
  for (const w of weekly) {
    const o = toMin(w.opens);
    const c = toMin(w.closes);
    if (c > o) out.push({ day: CONNECT_DAYS[w.day]!, start: hhmm(o), end: hhmm(c) });
    else if (c === o) out.push({ day: CONNECT_DAYS[w.day]!, start: hhmm(o), end: hhmm(o) });
    else {
      out.push({ day: CONNECT_DAYS[w.day]!, start: hhmm(o), end: "00:00" });
      out.push({ day: CONNECT_DAYS[(w.day + 1) % 7]!, start: "00:00", end: hhmm(c) });
    }
  }
  return sortEntries(out);
}

/**
 * Connect's entries as weekly windows - the seed for a schedule that
 * adopts what the instance already has. A window to midnight is kept as
 * such (23:59 would lose a minute); the past-midnight split is not
 * re-joined, so two entries stay two windows, which the bars draw fine.
 */
export function connectToWeekly(entries: ConnectHoursEntry[]): HoursSchedule["weekly"] {
  return sortEntries(entries).map((e) => ({ day: CONNECT_DAYS.indexOf(e.day), opens: e.start, closes: e.end === "00:00" ? "23:59" : e.end }));
}

export function sortEntries(entries: ConnectHoursEntry[]): ConnectHoursEntry[] {
  return [...entries].sort((a, b) => CONNECT_DAYS.indexOf(a.day) - CONNECT_DAYS.indexOf(b.day) || a.start.localeCompare(b.start) || a.end.localeCompare(b.end));
}

export function sameEntries(a: ConnectHoursEntry[], b: ConnectHoursEntry[]): boolean {
  const x = sortEntries(a);
  const y = sortEntries(b);
  return x.length === y.length && x.every((e, i) => e.day === y[i]!.day && e.start === y[i]!.start && e.end === y[i]!.end);
}

/**
 * How a linked schedule differs from what Connect has now (#527): the
 * windows and the zone are told apart, so the card can name a zone that
 * differs and offer to take Connect's without replacing the windows.
 */
export interface ConnectDrift {
  windows: boolean;
  timezone: boolean;
}

export function connectDrift(schedule: Pick<HoursSchedule, "weekly" | "timezone">, detail: HoursOfOperationDetail): ConnectDrift {
  return { windows: !sameEntries(weeklyToConnect(schedule.weekly), detail.config), timezone: schedule.timezone !== detail.timezone };
}

/**
 * The schedule once it is linked to a Connect hours of operation (#527):
 * the zone is Connect's, taken at the moment of linking, so the next sync
 * does not write the schedule's old zone over it. The windows stay as the
 * author had them - a difference there is drift to adopt or to write.
 * With no detail read yet, only the link changes.
 */
export function linkToConnect<T extends Pick<HoursSchedule, "timezone"> & { connect_hours?: string | undefined }>(schedule: T, name: string, detail: HoursOfOperationDetail | undefined): T {
  return { ...schedule, connect_hours: name, ...(detail ? { timezone: detail.timezone } : {}) };
}

/**
 * Adopting what Connect has (ADR-0019): its windows and its zone, both.
 */
export function adoptConnect<T extends Pick<HoursSchedule, "weekly" | "timezone">>(schedule: T, detail: HoursOfOperationDetail): T {
  return { ...schedule, timezone: detail.timezone, weekly: connectToWeekly(detail.config) };
}

/** "Mon-Fri 09:00-17:00 · Sat 10:00-14:00": the shape of a week in one line, for a card. */
export function describeEntries(entries: ConnectHoursEntry[]): string {
  if (entries.length === 0) return "closed all week";
  const short: Record<ConnectDay, string> = { SUNDAY: "Sun", MONDAY: "Mon", TUESDAY: "Tue", WEDNESDAY: "Wed", THURSDAY: "Thu", FRIDAY: "Fri", SATURDAY: "Sat" };
  const byWindow = new Map<string, ConnectDay[]>();
  for (const e of sortEntries(entries)) {
    const key = `${e.start}-${e.end === "00:00" ? "24:00" : e.end}`;
    (byWindow.get(key) ?? byWindow.set(key, []).get(key)!).push(e.day);
  }
  return [...byWindow.entries()]
    .map(([window, days]) => {
      const idx = days.map((d) => CONNECT_DAYS.indexOf(d)).sort((a, b) => a - b);
      const runs: string[] = [];
      let start = idx[0]!;
      let prev = idx[0]!;
      for (const i of idx.slice(1).concat(NaN)) {
        if (i === prev + 1) {
          prev = i;
          continue;
        }
        runs.push(start === prev ? short[CONNECT_DAYS[start]!] : `${short[CONNECT_DAYS[start]!]}-${short[CONNECT_DAYS[prev]!]}`);
        start = i;
        prev = i;
      }
      return `${runs.join(", ")} ${window}`;
    })
    .join(" · ");
}
